/**
 * Cockpit do turno — Consciência Situacional (Nível 0).
 * Execução hoje + o que requer atenção agora.
 */

import type { EnterpriseAction } from '../../../services/actionService';
import type { LstLideradoAderencia, LstRotinaDoDia, LstStatus } from '../types';
import { computeDayOeeMetrics } from './lstOeeMetrics';
import { formatExceptionCount, type TplSeverity } from './tplSeverityStyles';

export interface TplTurnExecution {
  planned: number;
  completed: number;
  pending: number;
  deviations: number;
  losses: number;
  disciplinePct: number;
}

export interface TplAttentionEvent {
  id: string;
  subject: string;
  detail: string;
  impact: number;
  severity: TplSeverity;
}

export interface TplSituationalCockpit {
  execution: TplTurnExecution;
  attentionNow: TplAttentionEvent[];
}

function isExecuted(status: LstStatus): boolean {
  return status === 'realizado' || status === 'realizado_com_desvio' || status === 'processo_parado';
}

export function computeTplSituationalCockpit(input: {
  today: string;
  rotinas: LstRotinaDoDia[];
  chainRows: LstLideradoAderencia[];
  correctiveActions: EnterpriseAction[];
  selfLabel: string;
}): TplSituationalCockpit {
  const {
    today,
    rotinas: rawRotinas,
    chainRows: rawChainRows,
    correctiveActions: rawActions,
    selfLabel,
  } = input;

  const rotinas = rawRotinas ?? [];
  const chainRows = rawChainRows ?? [];
  const correctiveActions = rawActions ?? [];

  const planned = rotinas.length;
  const completed = rotinas.filter((r) => isExecuted(r.status)).length;
  const deviations = rotinas.filter((r) => r.status === 'realizado_com_desvio').length;
  const losses = rotinas.filter((r) => r.status === 'nao_realizado').length;
  const pending = rotinas.filter((r) => !isExecuted(r.status)).length;

  const dayOee = computeDayOeeMetrics(rotinas.map((r) => ({ status: r.status })));

  const events: TplAttentionEvent[] = [];

  if (pending > 0) {
    events.push({
      id: 'self-pending',
      subject: selfLabel,
      detail: formatExceptionCount(pending, 'rotina pendente', 'rotinas pendentes')!,
      impact: pending * 15 + (rotinas.some((r) => r.template.is_critical && !isExecuted(r.status)) ? 20 : 0),
      severity: pending >= 3 ? 'critico' : 'atencao',
    });
  }

  for (const row of chainRows) {
    const dias = row.dias ?? [];
    const todayCell = dias.find((d) => d.date === today);
    if (!todayCell) continue;

    const leaderPending =
      todayCell.criticasTotal > 0
        ? todayCell.criticasTotal - todayCell.criticasRealizadas
        : 0;

    if (leaderPending > 0) {
      events.push({
        id: `leader-${row.member.id}-pending`,
        subject: row.member.name,
        detail: formatExceptionCount(leaderPending, 'rotina pendente', 'rotinas pendentes')!,
        impact: leaderPending * 12 + (todayCell.nivel === 'vermelho' ? 25 : 0),
        severity: todayCell.nivel === 'vermelho' ? 'critico' : 'atencao',
      });
    }

    if (todayCell.desvios > 0) {
      events.push({
        id: `leader-${row.member.id}-dev`,
        subject: row.member.name,
        detail: formatExceptionCount(todayCell.desvios, 'desvio no turno', 'desvios no turno')!,
        impact: todayCell.desvios * 8,
        severity: 'atencao',
      });
    }
  }

  const overdueActions = correctiveActions.filter(
    (a) =>
      a.status !== 'completed' &&
      a.status !== 'cancelled' &&
      a.due_date &&
      a.due_date < today,
  );

  for (const action of overdueActions.slice(0, 3)) {
    events.push({
      id: `action-${action.id}`,
      subject: action.assigned_name ?? action.title.slice(0, 40),
      detail: 'SLA de escalonamento vencido',
      impact: 90,
      severity: 'critico',
    });
  }

  for (const r of rotinas) {
    if (r.template.is_critical && r.status === 'nao_realizado') {
      events.push({
        id: `critical-${r.template.id}`,
        subject: r.template.title,
        detail: 'não realizada',
        impact: 85,
        severity: 'critico',
      });
    }
  }

  const deduped = new Map<string, TplAttentionEvent>();
  for (const e of events) {
    const key = `${e.subject}::${e.detail}`;
    const prev = deduped.get(key);
    if (!prev || e.impact > prev.impact) deduped.set(key, e);
  }

  const attentionNow = [...deduped.values()]
    .sort((a, b) => b.impact - a.impact)
    .slice(0, 5);

  return {
    execution: {
      planned,
      completed,
      pending,
      deviations,
      losses,
      disciplinePct: dayOee.disciplinaPct,
    },
    attentionNow,
  };
}
